import { useEffect, useMemo, useRef } from "react";
import { FormattedPaymentMethodTypes } from "types/paymentMethodComponent";
import { PaymentMethodList } from "types/payments";
import { getUsableSavedPaymentMethods } from "utils/paymentMethodAvailability";

interface UseDefaultPaymentMethodViewProps {
  cardList?: FormattedPaymentMethodTypes[];
  bankList?: FormattedPaymentMethodTypes[];
  walletList?: FormattedPaymentMethodTypes[];
  paymentMethodList?: Pick<PaymentMethodList, "name">[];
  paymentPermission?: Record<string, any> | null;
  isLogin: boolean;
  hasLoadedPaymentMethods: boolean;
  isActive?: boolean;
  setShowSavedMethods: (showSavedMethods: boolean) => void;
  setDataPayment?: React.Dispatch<React.SetStateAction<any>>;
  selectedMethod?: string;
  setSelectedMethod?: (method: string) => void;
}

const getMethodIdentity = (method: FormattedPaymentMethodTypes | undefined) =>
  method?.formatted?.id ??
  method?.id ??
  method?.cc_token ??
  method?.paypalid ??
  method?.account_email;

const isSelectedMethodUsable = (
  selectedMethod: FormattedPaymentMethodTypes | undefined,
  usableMethods: FormattedPaymentMethodTypes[]
) => {
  const selectedIdentity = getMethodIdentity(selectedMethod);
  return (
    selectedIdentity !== undefined &&
    usableMethods.some(
      (usableMethod) => getMethodIdentity(usableMethod) === selectedIdentity
    )
  );
};

export const useDefaultPaymentMethodView = ({
  cardList = [],
  bankList = [],
  walletList = [],
  paymentMethodList,
  paymentPermission,
  isLogin,
  hasLoadedPaymentMethods,
  isActive = true,
  setShowSavedMethods,
  setDataPayment,
  selectedMethod,
  setSelectedMethod,
}: UseDefaultPaymentMethodViewProps) => {
  const usableSavedPaymentMethods = useMemo(
    () =>
      getUsableSavedPaymentMethods({
        cardList,
        bankList,
        walletList,
        paymentMethodList,
        paymentPermission,
      }),
    [cardList, bankList, walletList, paymentMethodList, paymentPermission]
  );
  const appliedInitialMode = useRef(false);

  useEffect(() => {
    if (!isActive) {
      appliedInitialMode.current = false;
      return;
    }

    if (!isLogin) {
      setShowSavedMethods(false);
      return;
    }

    if (!hasLoadedPaymentMethods) return;

    setDataPayment?.((current: any) => {
      const next = { ...current };
      let changed = false;

      if (
        current?.selectedCard &&
        !isSelectedMethodUsable(
          current.selectedCard,
          usableSavedPaymentMethods.cards
        )
      ) {
        delete next.selectedCard;
        changed = true;
      }

      if (
        current?.selectedBank &&
        !isSelectedMethodUsable(
          current.selectedBank,
          usableSavedPaymentMethods.banks
        )
      ) {
        delete next.selectedBank;
        changed = true;
      }

      if (
        current?.selectedWallet &&
        !isSelectedMethodUsable(
          current.selectedWallet,
          usableSavedPaymentMethods.wallets
        )
      ) {
        delete next.selectedWallet;
        changed = true;
      }

      return changed ? next : current;
    });

    if (!usableSavedPaymentMethods.hasUsableSavedMethods) {
      setShowSavedMethods(false);

      const enabledMethodNames =
        paymentMethodList?.map(({ name }) => name) ?? [];
      if (
        enabledMethodNames.length > 0 &&
        (!selectedMethod || !enabledMethodNames.includes(selectedMethod))
      ) {
        setSelectedMethod?.(enabledMethodNames[0]);
      }

      return;
    }

    if (!appliedInitialMode.current) {
      setShowSavedMethods(true);
      appliedInitialMode.current = true;
    }
  }, [
    hasLoadedPaymentMethods,
    isActive,
    isLogin,
    paymentMethodList,
    selectedMethod,
    setDataPayment,
    setSelectedMethod,
    setShowSavedMethods,
    usableSavedPaymentMethods.banks,
    usableSavedPaymentMethods.cards,
    usableSavedPaymentMethods.hasUsableSavedMethods,
    usableSavedPaymentMethods.wallets,
  ]);

  return usableSavedPaymentMethods;
};
